import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL, PHOTO_BASE_URL_Admin } from '../../Api/config';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import GPTEDIcon from '../../Assets/icon/GPTEDIcon.png';
import tenderNotFoundImage from '../../Assets/images/tenderNotFoundImage.png';
import { ChevronDown } from "lucide-react"; // Import down arrow icon
const TenderSlider = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const [tendersData, setTendersData] = useState([[], [], [], []]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [visibleTenders, setVisibleTenders] = useState([]);
  const [slidesToShow, setSlidesToShow] = useState(3); // Default 3
  const [loading, setLoading] = useState(false);
  const [limit, setLimit] = useState(10);

  const tabs = [
    'Free Tenders',
    'Popular Tenders',
    'Hot Tenders',
    "Hot Int'l Tender",
  ];
  const tabsChanges = ['free', 'popular', 'hot', 'corrigendum', 'int_popular'];

  useEffect(() => {
    const fetchTendersData = async () => {
      setLoading(true);
      try {
        let url =
          activeTab === 0
            ? `${API_BASE_URL}/user/visitor/free`
            : `${API_BASE_URL}/user/visitor?type=${tabsChanges[activeTab]}`;

        const response = await axios.get(url);
        const filteredTenders = response.data?.data?.filter(
          tender => tender.status === 1 && tender.permission === 'publish'
        );

        const tenders = filteredTenders.slice(0, limit).map(tender => ({
          id: tender.id,
          company_logo: tender.company_logo,
          tender_id: tender.tender_id,
          name: tender.name,
          publish_on: tender.publish_on,
          submission_date: tender.submission_date,
          documents_price: tender.documents_price,
          file_upload: tender.file_upload,
        }));

        setTendersData(prevData => {
          const newData = [...prevData];
          newData[activeTab] = tenders;
          return newData;
        });

        setLoading(false);

        setVisibleTenders(tenders.slice(0, slidesToShow));
        setCurrentIndex(0);
      } catch (error) {
        console.error('Error fetching tenders:', error);
      }
    };

    fetchTendersData();
  }, [activeTab, slidesToShow]);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 640) {
        setSlidesToShow(1);
      } else if (window.innerWidth < 1024) {
        setSlidesToShow(2);
      } else {
        setSlidesToShow(3);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const nextSlide = () => {
    setCurrentIndex(prev => (prev + 1) % tendersData[activeTab].length);
  };

  const prevSlide = () => {
    setCurrentIndex(prev =>
      prev === 0 ? tendersData[activeTab].length - 1 : prev - 1
    );
  };

  useEffect(() => {
    setVisibleTenders(
      tendersData[activeTab].slice(currentIndex, currentIndex + slidesToShow)
    );
  }, [currentIndex, activeTab, slidesToShow, tendersData]);

  useEffect(() => {
    const interval = setInterval(nextSlide, 3000);
    return () => clearInterval(interval);
  }, [tendersData, activeTab]);

  const totalPages = Math.ceil(tendersData[activeTab].length / slidesToShow);
  const handlePageChange = page => {
    setCurrentIndex(page * slidesToShow);
  };

  const handleCardClick = tender_id => {
    navigate('/limitedTenderView', { state: { tender_id } });
    window.scrollTo(0, 0);
  };

  return (
    <div className="relative flex flex-col items-center bg-landingPageTenderSlider py-10 px-2">
      {/* Tabs */}
      <div className="w-full md:w-auto grid grid-cols-2 sm:grid-cols-4 md:grid-cols-4 justify-center gap-2 sm:gap-4 pb-2 mb-5">
        {tabs.map((tab, index) => (
          <button
            key={index}
            className={`
        w-full px-2 md:px-5 py-1 rounded transition-all duration-300 flex items-center justify-center gap-1
        ${activeTab === index
                ? 'bg-DefaultColor text-white shadow-lg border border-DefaultColor'
                : 'bg-slate-200 text-black'
              }
        text-[8px] sm:text-sm md:text-sm
      `}
            onClick={() => setActiveTab(index)}
          >
            {tab}
            {activeTab === index && <ChevronDown size={16} />}
          </button>
        ))}
      </div>
      {loading ? (
        <div className="w-full flex flex-col items-center justify-center py-10">
          <img
            src={tenderNotFoundImage}
            alt="No Tenders Found"
            className="w-80 h-80 object-contain"
          />
        </div>
      ) : (
        <div className="relative container mx-auto flex items-center justify-center w-full overflow-hidden">
          <button
            className="absolute left-1 top-1/2 transform -translate-y-1/2 bg-gray-200 rounded-full p-2 md:p-4 lg:p-4 shadow-md hover:bg-gray-300 z-10"
            onClick={prevSlide}
          >
            <FaChevronLeft />
          </button>

          <div className="overflow-hidden px-4">
            <div
              className="flex transition-transform duration-500 ease-in-out"
              style={{
                transform: `translateX(-${currentIndex * (100 / slidesToShow)
                  }%)`,
              }}
            >
              {tendersData[activeTab].length === 0 ? (
                <div className="w-full flex flex-col items-center justify-center py-10">
                  <img
                    src={tenderNotFoundImage}
                    alt="No Tenders Found"
                    className="w-80 h-80 object-contain"
                  />
                </div>
              ) : (
                tendersData[activeTab].map((tender, index) => {
                  const dataLength = tendersData[activeTab].length;
                  const isSingleData = dataLength === 1;
                  const isDoubleData = dataLength === 2;

                  return (
                    <div
                      onClick={() => {
                        if (activeTab === 0) {
                          navigate(`/pdf-viewer/${tender.id}`);
                          window.scrollTo(0, 0);
                        } else {
                          handleCardClick(tender.tender_id);
                        }
                      }}
                      className={`flex-shrink-0 md:px-5 px-3 container mx-auto cursor-pointer ${isSingleData
                        ? 'w-full'
                        : isDoubleData
                          ? 'sm:w-1/2'
                          : slidesToShow === 1
                            ? 'w-full'
                            : slidesToShow === 2
                              ? 'sm:w-1/2'
                              : 'lg:w-1/3'
                        }`}
                      key={index}
                    >
                      <div className="bg-white shadow-lg border rounded-lg h-full flex flex-col">
                        {/* Image Container */}
                        <div className="w-full h-40 flex-shrink-0">
                          <img
                            src={
                              tender.company_logo
                                ? `${PHOTO_BASE_URL_Admin}${tender.company_logo}`
                                : GPTEDIcon
                            }
                            alt="Tender Icon"
                            className="w-full h-full object-cover rounded-t-lg"
                          />
                        </div>

                        {/* Content */}
                        <div className="flex flex-col flex-grow p-4">
                          <div className="flex-grow">
                            {/* <h3 className="text-sm font-bold text-gray-800 mb-2 max-w-sm">
                              {tender.name}
                            </h3> */}

                            <p className="text-xs md:text-sm 2xl:text-lg text-gray-600 mt-2 line-clamp-2">
                              {tender.name}
                            </p>
                            <div className="my-2">
                              <span
                                className="text-blue-500 cursor-pointer relative group"
                              // title={tender.name}
                              >
                                See More
                                <div className="absolute hidden group-hover:block bg-gray-600 text-white text-sm rounded-lg shadow-lg p-2 z-20 bottom-full mt-2 left-1/2 transform -translate-x-1/2 w-64">
                                  {tender.name}
                                </div>
                              </span>
                            </div>

                            <p className="text-xs text-gray-600">
                              <strong>Tender ID:</strong> {tender.tender_id}
                            </p>
                            <p className="text-xs text-gray-600 mb-2">
                              <strong>Published On:</strong> {tender.publish_on}
                            </p>
                          </div>

                          {/* Button */}
                          <div className="mt-auto">
                            <button className="w-full bg-blue-600 text-white px-3 py-2 text-xs rounded-md shadow-md hover:bg-blue-700 transition-all">
                              {activeTab === 0 && tender.file_upload
                                ? '📄 View PDF'
                                : 'View Details'}
                            </button>
                          </div>
                        </div>
                      </div>
                      {/* old */}
                      {/* <div className="bg-white shadow-lg border rounded-lg h-auto">
                        <div className="w-full ">
                          <img
                            src={
                              tender.company_logo
                                ? `${PHOTO_BASE_URL_Admin}${tender.company_logo}`
                                : GPTEDIcon
                            }
                            alt="Tender Icon"
                            className="h-auto w-full object-cover rounded-t-lg"
                          />
                        </div>
                        <div className="flex flex-col p-4 h-auto justify-between">
                          <div>
                            <h3 className="text-sm font-bold text-gray-800 mb-2 max-w-sm">
                              {tender.name}
                            </h3>
                            <p className="text-xs text-gray-600">
                              <strong>Tender ID:</strong> {tender.tender_id}
                            </p>
                            <p className="text-xs text-gray-600 mb-2">
                              <strong>Published On:</strong> {tender.publish_on}
                            </p>
                          </div>

                          {activeTab === 0 && tender.file_upload ? (
                            <div className="mt-auto">
                              <button
                                className="w-full bg-blue-600 text-white px-3 py-2 text-xs rounded-md shadow-md hover:bg-blue-700 transition-all"
                              >
                                📄 View PDF
                              </button>
                            </div>
                          ) : (
                            <div className="mt-auto">
                              <button
                                className="w-full bg-blue-600 text-white px-3 py-2 text-xs rounded-md shadow-md hover:bg-blue-700 transition-all"
                              >
                                View Details
                              </button>
                            </div>
                          )}
                        </div>
                      </div> */}
                    </div>
                  );
                })
              )}
            </div>
          </div>

          <button
            className="absolute right-1 top-1/2 transform -translate-y-1/2 bg-gray-200 rounded-full p-2 md:p-4 lg:p-4 shadow-md hover:bg-gray-300 z-10"
            onClick={nextSlide}
          >
            <FaChevronRight />
          </button>
        </div>
      )}

      <div className="flex flex-col  gap-3 justify-center items-center  w-full container mx-auto px-4 mt-2 md:mt-5 lg:mt-6">
        {/* Pagination Dots - Left Side */}
        <div className="md:flex space-x-2 hidden">
          {Array.from({ length: totalPages }, (_, index) => (
            <button
              key={index}
              onClick={() => handlePageChange(index)}
              className={`w-3 h-3 rounded-full ${currentIndex / slidesToShow === index
                ? 'bg-blue-600 text-white'
                : 'bg-gray-300 text-black'
                }`}
            />
          ))}
        </div>

        {/* View More Button - Right Side */}
        <div className="bg-landingPageViewMoreButton rounded-lg px-3 py-1 mt-2">
          <a
            className="flex text-blue-600 justify-center items-center gap-2 text-xs"
            href="/allTenders"
          >
            View More Tender <FaChevronRight />
          </a>
        </div>
      </div>
    </div>
  );
};

export default TenderSlider;
